import {
  Button,
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  Input,
  cn,
} from "@base/ui";
import { ArrowRight } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Link } from "../../../shared/navegacao/navegacao";
import { useEntrar } from "../application/use-usuarios";
import { BOTAO_DE_ACESSO, CAMPO_DE_ACESSO, CabecalhoDeAcesso } from "./layout-de-acesso";
import { mensagemDoErro } from "../../../shared/http/mensagem-de-erro";

/**
 * Formulario de login, adaptado do `login-form` do bloco `login-02`.
 *
 * Sem "entrar com GitHub" nem "criar conta": quem cadastra usuario e o admin.
 */
export function FormularioDeLogin({
  aoEntrar,
  aviso,
  className,
}: {
  readonly aoEntrar: () => void;
  /** Mensagem de sucesso vinda de outra tela (ex.: senha definida). */
  readonly aviso?: string | null;
  readonly className?: string;
}) {
  const entrar = useEntrar();
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");

  function enviar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault();
    entrar.mutate({ email: email.trim().toLowerCase(), senha }, { onSuccess: aoEntrar });
  }

  return (
    <form className={cn("flex flex-col gap-6", className)} onSubmit={enviar} noValidate>
      <FieldGroup>
        <CabecalhoDeAcesso
          titulo="Entre no seu painel"
          descricao="Use o e-mail e a senha do acesso que você recebeu da equipe Monte Saúde."
        />
        {aviso && entrar.isIdle ? (
          <p role="status" className="rounded-xl bg-accent px-4 py-3 text-sm text-accent-foreground">
            {aviso}
          </p>
        ) : null}
        <Field data-invalid={entrar.isError || undefined}>
          <FieldLabel htmlFor="email" className="text-xs font-bold">E-mail</FieldLabel>
          <Input
            id="email"
            type="email"
            autoComplete="username"
            placeholder="voce@clinica.com.br"
            value={email}
            onChange={(evento) => setEmail(evento.target.value)}
            className={CAMPO_DE_ACESSO}
            required
          />
        </Field>
        <Field data-invalid={entrar.isError || undefined}>
          <div className="flex items-center">
            <FieldLabel htmlFor="senha" className="text-xs font-bold">Senha</FieldLabel>
            <Link
              para="/esqueci-senha"
              className="ml-auto text-xs font-bold text-foreground underline-offset-4 hover:underline"
            >
              Esqueceu a senha?
            </Link>
          </div>
          <Input
            id="senha"
            type="password"
            autoComplete="current-password"
            value={senha}
            onChange={(evento) => setSenha(evento.target.value)}
            className={CAMPO_DE_ACESSO}
            required
          />
        </Field>
        {entrar.isError ? <FieldError>{mensagemDoErro(entrar.error)}</FieldError> : null}
        <Field>
          <Button
            type="submit"
            className={BOTAO_DE_ACESSO}
            disabled={entrar.isPending || !email || !senha}
          >
            {entrar.isPending ? "Entrando…" : "Entrar"}
            <ArrowRight aria-hidden="true" />
          </Button>
          <FieldDescription className="text-center text-xs">
            Ainda não tem acesso? Fale com a equipe Monte Saúde.
          </FieldDescription>
        </Field>
      </FieldGroup>
    </form>
  );
}
